import { GenericAbortSignal } from 'axios';

import { Paginated } from 'models/flexible/Paginated';
import type {
  AdminImportIndexFields,
  AdminImportIndexInclude,
  AdminImportShowFields,
  AdminImportShowInclude,
  AdminImportUpdateParams,
} from 'services/api/fields/admin/import';
import type { Typed } from 'services/api/fields/core';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export const adminImportsQueryKeys = {
  index: (params?: Record<string, any>) => ['adminImports', params] as const,
  show: (id: string) => ['adminImports', id] as const,
};

export enum ImportStatus {
  PENDING = 'pending',
  RUNNING = 'running',
  PAUSED = 'paused',
  CANCELED = 'canceled',
  COMPLETED = 'completed',
  ERRORED = 'errored',
}

export enum ImportAction {
  CANCEL = 'cancel',
  RESTART = 'restart',
  RUN = 'run',
}

const ALLOWED_STATUS_ACTIONS = new Map<ImportStatus, ImportAction[]>([
  [ImportStatus.CANCELED, [ImportAction.RESTART]],
  [ImportStatus.COMPLETED, [ImportAction.RESTART]],
  [ImportStatus.ERRORED, [ImportAction.RESTART]],
  [ImportStatus.PAUSED, [ImportAction.CANCEL, ImportAction.RUN]],
  [ImportStatus.PENDING, [ImportAction.CANCEL]],
  [ImportStatus.RUNNING, [ImportAction.CANCEL]],
]);

export type StepCount = { fileRowCount: number; memberCount: number };

export enum ImportStepStatus {
  COMPLETED = 'completed',
  ERROR = 'error',
  PENDING = 'pending',
  RUNNING = 'running',
}

export enum ErrorType {
  ERROR = 'error',
  WARNING = 'warning',
}

export type StepResultsErrorMessage = {
  type: string;
  class: string;
  rowNumbers: number[];
  message: string;
  totalCount?: number;
  link?: string;
};

export type NewAttrValueCount = { key: string; value: number };

export type StepResults = {
  errorMsgs: StepResultsErrorMessage[];
  newAttrNames?: string[];
  newAttrValueCounts?: NewAttrValueCount[];
  fileRowCount?: [] | number[];
  importedRowCount?: [] | number[];
  memberCount?: [] | number[];
};

export const IMPORT_PROCESS_STEPS: Record<string, string> = {
  create_import_rows: 'Create Import Rows',
  create_attrs_and_values: 'Create Attributes and Values',
  create_imported_patients: 'Create Imported Patients',
  track_removed_from_census: 'Track Removed from Census',
  update_patients: 'Update Patients',
  create_patients: 'Create Patients',
  cleanup: 'Cleanup',
  validate_file: 'Validate File',
};

export type ImportStepOptions = {
  apiType: string;
  order: number;
  status: string;
  results: StepResults;
};

export class ImportStep {
  apiType: string;
  order: number;
  status: string;
  results: StepResults;

  constructor(opts: ImportStepOptions) {
    const defaults = { apiType: '', order: 0, status: '', results: { errorMsgs: [] } };
    const o = { ...defaults, ...opts };
    this.apiType = o.apiType;
    this.order = o.order;
    this.status = o.status;
    this.results = o.results;
  }

  get errors() {
    return this.results.errorMsgs.filter((msg) => msg.type === ErrorType.ERROR);
  }

  get warnings() {
    return this.results.errorMsgs.filter((msg) => msg.type === ErrorType.WARNING);
  }

  get hasResults() {
    return Boolean(Object.values(this.results).flat().filter(Boolean).length);
  }
}

export type AdminImportOptions = {
  id: string;
  status: string;
  statusMessage: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  [key: string]: any;
};

// eslint-disable-next-line prefer-const
let AdminImport: new <T extends AdminImportOptions>(opts: T) => Typed<_AdminImport<T>, T>;

class _AdminImport<T extends AdminImportOptions = AdminImportOptions> {
  status!: ImportStatus;
  stepCounts!: StepCount;
  steps!: ImportStep[];
  createdAt!: string | null;
  child!: AdminImport<NonNullable<T['child']>> | null;

  constructor(opts: T) {
    Object.assign(this, opts);

    this.status = opts.status as ImportStatus;
    this.stepCounts = opts.stepCounts ?? { fileRowCount: 0, memberCount: 0 };
    this.steps = (opts.steps ?? []).map((s) => new ImportStep(s));
    this.createdAt = opts.createdAt;
    this.child = opts.child ? new AdminImport<NonNullable<T['child']>>(opts.child) : null;
  }

  get runnable() {
    return ALLOWED_STATUS_ACTIONS.get(this.status)?.includes(ImportAction.RUN);
  }

  get restartable() {
    return !this.child && ALLOWED_STATUS_ACTIONS.get(this.status)?.includes(ImportAction.RESTART) && !this.fileExpired;
  }

  get cancellable() {
    return ALLOWED_STATUS_ACTIONS.get(this.status)?.includes(ImportAction.CANCEL);
  }

  get fileExpired() {
    if (!this.createdAt) return true;
    const created = new Date(this.createdAt);
    const expires = new Date(created.getTime() + 7 * 24 * 60 * 60 * 1000);
    return new Date() > expires;
  }
}

AdminImport = _AdminImport as typeof AdminImport;

export { AdminImport };
export type AdminImport<T extends AdminImportOptions = AdminImportOptions> = _AdminImport<T> &
  Omit<T, keyof _AdminImport<T>>;

export type AdminImportIndexParams = BaseIndexQueryParams & {
  client?: string;
  status?: string;
  'createdAt.gteq'?: string;
  'createdAt.lteq'?: string;
};

export async function adminIndexImports<I extends AdminImportIndexInclude = never>(
  params: AdminImportIndexParams,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<Paginated<AdminImport<AdminImportIndexFields<I>>>> {
  const include = includes?.length ? includes.join(',') : undefined;
  return http
    .get<Paginated<AdminImportOptions>>('admin/imports', { params: { ...params, include }, signal })
    .then((res) => ({
      ...res.data,
      data: res.data.data.map((x) => new AdminImport(x)),
    })) as unknown as Promise<Paginated<AdminImport<AdminImportIndexFields<I>>>>;
}

export async function adminShowImport<I extends AdminImportShowInclude = never>(
  id: string,
  signal?: GenericAbortSignal,
  includes?: I[]
): Promise<AdminImport<AdminImportShowFields<I>>> {
  const include = includes?.length ? includes.join(',') : undefined;
  return http
    .get<AdminImportOptions>(`admin/imports/${id}`, { params: { include }, signal })
    .then((res) => new AdminImport(res.data)) as unknown as Promise<AdminImport<AdminImportShowFields<I>>>;
}

export async function adminUpdateImport(id: string, params: AdminImportUpdateParams) {
  return http.patch<AdminImportOptions>(`admin/imports/${id}`, params).then((res) => new AdminImport(res.data));
}
